import React from 'react';
import { ParagraphText, Tabs } from '@leagueplatform/genesis-core';
import { Box, Flex } from '@leagueplatform/genesis-commons';
import { SectionHeader } from '@leagueplatform/genesis-core-alpha';
import { Formatter, useIntl } from '@leagueplatform/locales';
import {
  PillTab,
  PillTabList,
  PillTabs,
  LoadingIndicator,
  ErrorState,
} from '@leagueplatform/web-common-components';
import {
  SHORT_DATE_AND_TIME_FORMAT,
  SHORT_DATE_FORMAT,
} from '@leagueplatform/web-common';
import { useWalletParams } from '#hooks/use-wallet-params.hook.ts';
import { NetworkPlan } from './components/network-plan/network-plan.component';

import { MemberDropDown } from '../member-dropdown/member-dropdown.component';
import { useMemberDropdown } from './hooks/use-member-dropdown.hook';
import { usePlanProgressV1 } from './hooks/use-plan-progress-v1.hook';
import { NETWORK_PLAN_ID } from './types/plan-progress.types';

const ACCUMULATION_PERIOD_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  ...SHORT_DATE_FORMAT,
  timeZone: 'UTC',
};

export const PlanProgressV1 = () => {
  const { formatDate, formatMessage, $tmap } = useIntl();
  const { userId, benefitId } = useWalletParams();

  const {
    plans,
    accumulationPeriod,
    lastUpdated,
    carrierName,
    onTabClickEvent: handleTabClickEvent,
    onAccordionToggleEvent: handleAccordionToggleEvent,
    isLoading,
    isError,
    refetch,
    error,
  } = usePlanProgressV1(userId, benefitId!);

  const handleButtonClick = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const { showMemberSelect } = useMemberDropdown();

  const getPlanIDMessage = $tmap<NETWORK_PLAN_ID>({
    [NETWORK_PLAN_ID.IN_NETWORK]: 'IN_NETWORK',
    [NETWORK_PLAN_ID.OUT_OF_NETWORK]: 'OUT_OF_NETWORK',
    [NETWORK_PLAN_ID.NO_NETWORK]: 'NETWORK_PLAN_ID',
  });

  const strong: Formatter = (msg) => <Box as="strong">{msg}</Box>;

  return (
    <Box as="section">
      <>
        {showMemberSelect && <MemberDropDown />}
        <SectionHeader
          title={formatMessage({ id: 'PLAN_PROGRESS' })}
          headingLevel="2"
          css={{
            marginBottom: '$oneAndHalf',
            marginTop: showMemberSelect ? '$oneAndHalf' : '$none',
          }}
        />
      </>
      {isLoading && <LoadingIndicator inline />}
      {isError && !isLoading && (
        <Flex
          as="section"
          backgroundColor="surface.background.tertiary"
          padding="one"
          minHeight={286}
          textAlign="center"
          alignItems="center"
          justifyContent="center"
        >
          <ErrorState
            hideImage
            onButtonClick={handleButtonClick}
            error={error}
          />
        </Flex>
      )}
      {!isLoading && !isError && (
        <>
          {accumulationPeriod && (
            <ParagraphText size="sm">
              {formatMessage(
                { id: 'ACCUMULATION_PERIOD' },
                {
                  start: formatDate(
                    accumulationPeriod.start,
                    ACCUMULATION_PERIOD_DATE_FORMAT,
                  ),
                  end: formatDate(
                    accumulationPeriod.end,
                    ACCUMULATION_PERIOD_DATE_FORMAT,
                  ),
                  strong,
                },
              )}
            </ParagraphText>
          )}
          <PillTabs
            onValueChange={(value) => {
              handleTabClickEvent(
                plans[plans.findIndex((plan) => plan.id === value)].analyticsId,
              );
            }}
            defaultValue={plans[0].id}
          >
            <Box width="fit-content" marginY="twoAndHalf">
              <PillTabList>
                {plans.map((tab) => (
                  <PillTab key={tab.id} value={tab.id}>
                    {getPlanIDMessage(tab.id)}
                  </PillTab>
                ))}
              </PillTabList>
            </Box>

            {plans.map((plan) => (
              <Tabs.TabPanel key={plan.id} value={plan.id}>
                <NetworkPlan
                  networkProviders={plan.network}
                  networkName={getPlanIDMessage(plan.id)}
                  lastUpdated={formatMessage(
                    { id: 'LAST_UPDATED_CARRIER_DATE' },
                    {
                      date: formatDate(lastUpdated, SHORT_DATE_AND_TIME_FORMAT),
                      carrier: carrierName,
                    },
                  )}
                  onProviderToggle={handleAccordionToggleEvent}
                />
              </Tabs.TabPanel>
            ))}
          </PillTabs>
        </>
      )}
    </Box>
  );
};
